import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Link,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Add, Delete } from "@mui/icons-material";
import { useState } from "react";

const AchievementsStep = ({
  profileData,
  handleSimpleArrayAdd,
  handleSimpleArrayRemove,
  handleAddItem,
  handleDeleteItem,
  currentPublication,
  setCurrentPublication,
  initialPublicationState,
}) => {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme(); // CHANGED: Instantiate theme
  const [currentAchievement, setCurrentAchievement] = useState("");
  const [currentContribution, setCurrentContribution] = useState("");
  const [currentHobby, setCurrentHobby] = useState("");
  const [currentReference, setCurrentReference] = useState("");
  const [currentActivity, setCurrentActivity] = useState("");

  // CHANGED: Removed all local style objects (cardStyles, titleStyles, etc.)

  const cardStyles = {
    p: 3,
    borderRadius: "24px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    bgcolor: "background.paper",
    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
  };


  const titleStyles = {
    color: "primary.main",
    fontWeight: "bold",
    mb: 2,
  };

  const sectionButtonStyles = {
    whiteSpace: "nowrap",
    ml: 1,
    height: "56px",
    borderRadius: "12px",
    px: 3,
    fontWeight: "bold",
    boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
  };


  const handleAddSimpleItem = (field, value, setValue) => {
    if (value.trim()) {
      handleSimpleArrayAdd(field, value);
      setValue("");
    }
  };

  const handleAddPublicationClick = () => {
    if (currentPublication.name.trim()) {
      handleAddItem("publications", currentPublication);
      setCurrentPublication(initialPublicationState);
    }
  };

  const renderSimpleList = (list, fieldName) => (
    <List dense>
      {Array.isArray(list) &&
        list.map((item, index) => (
          <ListItem
            key={index}
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => handleSimpleArrayRemove(fieldName, index)}
                color="primary" // CHANGED: Use color prop
              >
                <Delete />
              </IconButton>
            }
          >
            <ListItemText primary={item} />
          </ListItem>
        ))}
    </List>
  );

  return (
    // CHANGED: Replaced hardcoded color with theme variable
    <Box sx={{ backgroundColor: "background.paper", p: 0 }}>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Showcase your achievements, publications, and additional experiences.
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={cardStyles}>
            <Typography variant="h6" sx={titleStyles}>
              Awards & Achievements
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                label="Add Award And Achievement" // CHANGED: Used label instead of placeholder
                value={currentAchievement}
                onChange={(e) => setCurrentAchievement(e.target.value)}
                // CHANGED: sx prop removed
              />
              <Button
                startIcon={<Add />}
                sx={sectionButtonStyles}
                variant="contained" // CHANGED
                color="primary" // CHANGED
                onClick={() =>
                  handleAddSimpleItem(
                    "awards",
                    currentAchievement,
                    setCurrentAchievement
                  )
                }
              >
                Add
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {renderSimpleList(profileData.awards, "awards")}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardStyles}>
            <Typography variant="h6" sx={titleStyles}>
              Publications
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Publication Name" // CHANGED
                  value={currentPublication.name}
                  onChange={(e) =>
                    setCurrentPublication({
                      ...currentPublication,
                      name: e.target.value,
                    })
                  }
                  // CHANGED: sx prop removed
                />
                <TextField
                  fullWidth
                  label="Publication URL" // CHANGED
                  value={currentPublication.link}
                  onChange={(e) =>
                    setCurrentPublication({
                      ...currentPublication,
                      link: e.target.value,
                    })
                  }
                  // CHANGED: sx prop removed
                />
              </Box>
              <Button
                startIcon={<Add />}
                sx={{
                  ...sectionButtonStyles,
                  alignSelf: "flex-end",
                  mt: 1,
                  ml: 0,
                }}
                variant="contained"
                color="primary"
                onClick={handleAddPublicationClick}
              >
                Add Publication
              </Button>

            </Stack>
            <Box sx={{ flexGrow: 1, mt: 2, overflow: "auto" }}>
              <List dense>
                {profileData.publications &&
                  profileData.publications.map((pub, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() =>
                            handleDeleteItem("publications", index)
                          }
                          color="primary" // CHANGED
                        >
                          <Delete />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={pub.name}
                        secondary={
                          pub.link ? (
                            // CHANGED: Used Link component for theming
                            <Link
                              href={pub.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="secondary"
                            >
                              {pub.link}
                            </Link>
                          ) : null
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardStyles}>
            <Typography variant="h6" sx={titleStyles}>
              Open Source Contributions
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                label="Add Open Source Contribution" // CHANGED
                value={currentContribution}
                onChange={(e) => setCurrentContribution(e.target.value)}
                // CHANGED: sx prop removed
              />
              <Button
                startIcon={<Add />}
                sx={sectionButtonStyles}
                variant="contained" // CHANGED
                color="primary" // CHANGED
                onClick={() =>
                  handleAddSimpleItem(
                    "open_source_contributions",
                    currentContribution,
                    setCurrentContribution
                  )
                }
              >
                Add
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {renderSimpleList(
                profileData.open_source_contributions,
                "open_source_contributions"
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardStyles}>
            <Typography variant="h6" sx={titleStyles}>
              Interests & Hobbies
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                label="Add Interest and Hobby" // CHANGED
                value={currentHobby}
                onChange={(e) => setCurrentHobby(e.target.value)}
                // CHANGED: sx prop removed
              />
              <Button
                startIcon={<Add />}
                sx={sectionButtonStyles}
                variant="contained" // CHANGED
                color="primary" // CHANGED
                onClick={() =>
                  handleAddSimpleItem(
                    "interests",
                    currentHobby,
                    setCurrentHobby
                  )
                }
              >
                Add
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {renderSimpleList(profileData.interests, "interests")}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardStyles}>
            <Typography variant="h6" sx={titleStyles}>
              References
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                label="Add Reference" // CHANGED
                value={currentReference}
                onChange={(e) => setCurrentReference(e.target.value)}
                // CHANGED: sx prop removed
              />
              <Button
                startIcon={<Add />}
                sx={sectionButtonStyles}
                variant="contained" // CHANGED
                color="primary" // CHANGED
                onClick={() =>
                  handleAddSimpleItem(
                    "references",
                    currentReference,
                    setCurrentReference
                  )
                }
              >
                Add
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {renderSimpleList(profileData.references, "references")}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardStyles}>
            <Typography variant="h6" sx={titleStyles}>
              Additional Information
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                fullWidth
                label="Extracurricular Activity" // CHANGED
                value={currentActivity}
                onChange={(e) => setCurrentActivity(e.target.value)}
                // CHANGED: sx prop removed
              />
              <Button
                startIcon={<Add />}
                sx={sectionButtonStyles}
                variant="contained" // CHANGED
                color="primary" // CHANGED
                onClick={() =>
                  handleAddSimpleItem(
                    "extracurriculars",
                    currentActivity,
                    setCurrentActivity
                  )
                }
              >
                Add
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              {renderSimpleList(
                profileData.extracurriculars,
                "extracurriculars"
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AchievementsStep;
